import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { Subject, debounceTime, finalize, forkJoin, of, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ActionButton, ActionButtonsComponent } from 'src/app/components/shared/action-buttons/action-buttons.component';
import { PositionDistributionChartComponent } from 'src/app/components/seo/position-distribution-chart/position-distribution-chart.component';
import { InMemoryCacheService, UtilsService } from 'src/app/common';
import { PermissionsService } from 'src/app/common/services/permissions.service';
import { OrganizationSettingsService } from 'src/app/pages/organization-settings/services/organization-settings.service';
import {
  SeoUsageBySourceItem,
  SeoUsageSummaryResponse,
  SeoUsageTimelineItem,
  SeoUsageTrackingPayload
} from './seo-usage.models';
import { SeoUsageApiService } from './seo-usage-api.service';

interface SeoUsageFilterForm {
  from: FormControl<Date | null>;
  to: FormControl<Date | null>;
}

@Component({
  selector: 'gig-seo-usage',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CalendarModule,
    DropdownModule,
    ActionButtonsComponent,
    TableModule,
    PositionDistributionChartComponent,
    ProgressSpinnerModule
  ],
  templateUrl: './seo-usage.component.html',
  styleUrl: './seo-usage.component.scss'
})
export class SeoUsageComponent implements OnInit, OnDestroy {
  private readonly maxRangeDays: number = 180;
  private readonly applySubject: Subject<void> = new Subject<void>();
  private readonly destroy$: Subject<void> = new Subject<void>();

  public readonly filterForm: FormGroup<SeoUsageFilterForm> = this.buildForm();

  public organizationId: number = 0;
  public organizationName: string = '';
  public canAccessPage: boolean = false;
  public isForbiddenContext: boolean = false;

  public isLoading: boolean = false;
  public isLoadedOnce: boolean = false;
  public validationMessage: string | null = null;
  public errorMessage: string | null = null;

  public summary: SeoUsageSummaryResponse = this.emptySummary();
  public effectiveFrom: string | undefined;
  public effectiveTo: string | undefined;
  public availableSources: string[] = [];
  public selectedSources: string[] = [];
  public selectedChartMetric: 'requestCount' | 'totalItemsCount' = 'requestCount';
  public readonly chartMetricOptions: { label: string; value: string }[] = [
    { label: 'Requests', value: 'requestCount' },
    { label: 'Total Items', value: 'totalItemsCount' }
  ];
  public chartPositionNames: Record<string, string> = {};
  public chartColors: any[] = [];
  public sourceTrendChartData: Array<{ date: string; [key: string]: number | string }> = [];
  public isSourceChartLoading: boolean = false;

  private readonly sourceTimelinesBySource: Map<string, SeoUsageTimelineItem[]> = new Map<string, SeoUsageTimelineItem[]>();

  public readonly applyActionButtons: ActionButton[] = [
    {
      id: 'applySeoUsageFilters',
      label: 'Apply',
      type: 'confirm',
      icon: 'pi-filter',
      iconPos: 'left',
      color: 'success',
      size: 'medium'
    }
  ];

  public readonly refreshActionButtons: ActionButton[] = [
    {
      id: 'refreshSeoUsageFilters',
      label: 'Refresh',
      type: 'confirm',
      icon: 'pi-refresh',
      iconPos: 'left',
      color: 'secondary',
      size: 'medium'
    }
  ];

  public timelineChartData: any = { labels: [], datasets: [] };
  public timelineChartOptions: any = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom'
      }
    }
  };

  public constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly seoUsageApiService: SeoUsageApiService,
    private readonly permissionsService: PermissionsService,
    private readonly organizationSettingsService: OrganizationSettingsService,
    private readonly utils: UtilsService,
    private readonly cache: InMemoryCacheService
  ) {}

  public async ngOnInit(): Promise<void> {
    this.organizationId = Number(this.route.snapshot.paramMap.get('orgId'));

    if (!Number.isFinite(this.organizationId) || this.organizationId <= 0) {
      this.errorMessage = 'Organization id is missing from the route.';
      return;
    }

    this.organizationName = this.resolveOrganizationName(this.organizationId);

    await this.evaluateContextAccess();
    if (!this.canAccessPage) {
      return;
    }

    this.applySubject
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        void this.loadSummary();
      });

    this.chartColors = this.utils.transparentChartColors;

    this.trackEvent('seo_usage_page_viewed', { organizationId: this.organizationId });
    void this.loadSummary();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onApplyFilters(): void {
    this.validationMessage = this.getValidationMessage();

    if (this.validationMessage) {
      this.trackEvent('seo_usage_validation_error', {
        organizationId: this.organizationId,
        from: this.getFromIso(),
        to: this.getToIso(),
        reason: this.validationMessage
      });
      return;
    }

    this.applySubject.next();
  }

  public onRefresh(): void {
    if (!this.canAccessPage) {
      return;
    }

    this.trackEvent('seo_usage_filter_applied', {
      organizationId: this.organizationId,
      from: this.getFromIso(),
      to: this.getToIso(),
      reason: 'manual_refresh'
    });
    void this.loadSummary();
  }

  public onChartMetricChanged(metric: 'requestCount' | 'totalItemsCount'): void {
    this.selectedChartMetric = metric;
    this.rebuildSourceTrendChartData();
    this.trackEvent('seo_usage_chart_metric_changed', {
      organizationId: this.organizationId,
      from: this.getFromIso(),
      to: this.getToIso(),
      metric
    });
  }

  public onSelectedSourcesChanged(values: string[]): void {
    this.selectedSources = values?.length ? values : [...this.availableSources];
    void this.loadSourceTrendData(this.getFromIso(), this.getToIso());
    this.trackEvent('seo_usage_chart_sources_changed', {
      organizationId: this.organizationId,
      from: this.getFromIso(),
      to: this.getToIso(),
      reason: `source_count_${this.selectedSources.length}`
    });
  }

  public onActionButtonClick(event: any): void {
    if (event?.id === 'applySeoUsageFilters') {
      this.onApplyFilters();
      return;
    }

    if (event?.id === 'refreshSeoUsageFilters') {
      this.onRefresh();
    }
  }

  public getRangeLabel(): string {
    if (!this.effectiveFrom && !this.effectiveTo) {
      return 'Default backend range (last 30 days)';
    }

    const fromLabel: string = this.effectiveFrom ? this.formatIsoDate(this.effectiveFrom) : 'Open';
    const toLabel: string = this.effectiveTo ? this.formatIsoDate(this.effectiveTo) : 'Open';

    return `${fromLabel} to ${toLabel}`;
  }

  private buildForm(): FormGroup<SeoUsageFilterForm> {
    return new FormGroup<SeoUsageFilterForm>({
      from: new FormControl<Date | null>(null),
      to: new FormControl<Date | null>(null)
    });
  }

  private async evaluateContextAccess(): Promise<void> {
    const role: string = (this.cache.auth?.user?.user?.role ?? '').toString();
    const isSuperUser: boolean = role.toLowerCase() === 'superuser';
    const currentOrgId: number = Number(this.cache.auth?.organization?.id);

    if (!isSuperUser && currentOrgId && currentOrgId !== this.organizationId) {
      this.isForbiddenContext = true;
      this.canAccessPage = false;
      await this.router.navigate(['/access-denied']);
      return;
    }

    this.canAccessPage = await this.permissionsService.canAsync('Org.Audit.View');
    if (!this.canAccessPage) {
      this.isForbiddenContext = true;
      await this.router.navigate(['/access-denied']);
    }
  }

  private resolveOrganizationName(orgId: number): string {
    const organizations = this.organizationSettingsService.organizations?.() ?? [];
    const orgFromList = organizations.find(org => org.id === orgId);
    if (orgFromList?.name) {
      return orgFromList.name;
    }

    const cachedOrg = this.cache.auth?.organization;
    if (Number(cachedOrg?.id) === orgId && cachedOrg?.name) {
      return cachedOrg.name;
    }

    return `Organization ${orgId}`;
  }

  private loadSummary(): void {
    this.validationMessage = null;
    this.errorMessage = null;
    this.isLoading = true;

    const from: string | undefined = this.getFromIso();
    const to: string | undefined = this.getToIso();

    this.trackEvent('seo_usage_filter_applied', {
      organizationId: this.organizationId,
      from,
      to
    });

    this.seoUsageApiService
      .getSeoUsageSummary(this.organizationId, from, to)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.isLoadedOnce = true;
        })
      )
      .subscribe({
        next: (response: SeoUsageSummaryResponse) => {
          this.sourceTimelinesBySource.clear();
          this.summary = {
            ...response,
            sources: this.sortSourcesByRequests(response.sources),
            timeline: response.timeline ?? []
          };

          const uniqueSources = new Set(this.summary.sources.map(item => item.source).filter(source => !!source));
          this.availableSources = Array.from(uniqueSources);
          this.selectedSources = [...this.availableSources];
          this.chartPositionNames = this.buildChartPositionNames(this.selectedSources);

          this.effectiveFrom = response.from;
          this.effectiveTo = response.to;
          this.sourceTrendChartData = [];
          void this.loadSourceTrendData(from, to);
        },
        error: (error: HttpErrorResponse) => {
          this.summary = this.emptySummary();
          this.availableSources = [];
          this.selectedSources = [];
          this.chartPositionNames = {};
          this.sourceTrendChartData = [];
          this.handleError(error, from, to);
        }
      });
  }

  private handleError(error: HttpErrorResponse, from?: string, to?: string): void {
    if (error.status === 400) {
      this.errorMessage = 'Date range is invalid. Ensure From is before To and range is not greater than 180 days.';
    } else if (error.status === 403) {
      this.isForbiddenContext = true;
      this.errorMessage = 'You do not have access to this organization usage view.';
      void this.router.navigate(['/access-denied']);
    } else {
      this.errorMessage = 'We could not load SEO usage right now. Please retry.';
    }

    this.trackEvent('seo_usage_api_error', {
      organizationId: this.organizationId,
      from,
      to,
      reason: `status_${error.status || 'unknown'}`
    });
  }

  private getValidationMessage(): string | null {
    const fromDate: Date | null = this.filterForm.controls.from.value;
    const toDate: Date | null = this.filterForm.controls.to.value;

    if (!fromDate || !toDate) {
      return null;
    }

    if (fromDate.getTime() > toDate.getTime()) {
      return 'From date must be before or equal to To date.';
    }

    const dayDifference: number = Math.floor((toDate.getTime() - fromDate.getTime()) / 86400000) + 1;
    if (dayDifference > this.maxRangeDays) {
      return 'Date range cannot be greater than 180 days.';
    }

    return null;
  }

  private getFromIso(): string | undefined {
    const fromDate: Date | null = this.filterForm.controls.from.value;
    return fromDate ? fromDate.toISOString() : undefined;
  }

  private getToIso(): string | undefined {
    const toDate: Date | null = this.filterForm.controls.to.value;
    return toDate ? toDate.toISOString() : undefined;
  }

  private async loadSourceTrendData(from?: string, to?: string): Promise<void> {
    const activeSources = this.selectedSources?.length ? this.selectedSources : [...this.availableSources];
    if (!activeSources.length) {
      this.sourceTrendChartData = [];
      this.chartPositionNames = {};
      return;
    }

    this.isSourceChartLoading = true;

    const missingSources = activeSources.filter(source => !this.sourceTimelinesBySource.has(source));
    if (missingSources.length) {
      const requests = missingSources.map(source =>
        this.seoUsageApiService.getSeoUsageSummary(this.organizationId, from, to, source).pipe(
          catchError(() => of({ ...this.emptySummary(), timeline: [] as SeoUsageTimelineItem[] }))
        )
      );

      forkJoin(requests)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: responses => {
            responses.forEach((response, index) => {
              this.sourceTimelinesBySource.set(missingSources[index], response.timeline ?? []);
            });
            this.chartPositionNames = this.buildChartPositionNames(activeSources);
            this.rebuildSourceTrendChartData();
            this.isSourceChartLoading = false;
          },
          error: () => {
            this.chartPositionNames = this.buildChartPositionNames(activeSources);
            this.rebuildSourceTrendChartData();
            this.isSourceChartLoading = false;
          }
        });

      return;
    }

    this.chartPositionNames = this.buildChartPositionNames(activeSources);
    this.rebuildSourceTrendChartData();
    this.isSourceChartLoading = false;
  }

  private rebuildSourceTrendChartData(): void {
    const activeSources = this.selectedSources?.length ? this.selectedSources : [...this.availableSources];
    if (!activeSources.length) {
      this.sourceTrendChartData = [];
      return;
    }

    const dateMap = new Map<string, { date: string; [key: string]: number | string }>();
    const useRequests = this.selectedChartMetric === 'requestCount';

    activeSources.forEach(source => {
      const timeline = this.sourceTimelinesBySource.get(source) ?? [];
      timeline.forEach(item => {
        const dateKey = item.date;
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { date: dateKey });
        }

        const row = dateMap.get(dateKey);
        if (row) {
          row[source] = useRequests ? item.requestCount : item.totalItemsCount;
        }
      });
    });

    const rows = Array.from(dateMap.values())
      .sort((a, b) => new Date(String(a.date)).getTime() - new Date(String(b.date)).getTime())
      .map(row => {
        activeSources.forEach(source => {
          if (typeof row[source] !== 'number') {
            row[source] = 0;
          }
        });

        return row;
      });

    this.sourceTrendChartData = rows;
  }

  private formatIsoDate(value: string): string {
    const date: Date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString();
  }

  private buildChartPositionNames(sources: string[]): Record<string, string> {
    return (sources ?? []).reduce((acc, source) => {
      acc[source] = source;
      return acc;
    }, {} as Record<string, string>);
  }

  private sortSourcesByRequests(items: SeoUsageBySourceItem[]): SeoUsageBySourceItem[] {
    return [...(items ?? [])].sort((a, b) => b.requestCount - a.requestCount);
  }

  private emptySummary(): SeoUsageSummaryResponse {
    return {
      organizationId: this.organizationId,
      requestCount: 0,
      totalCost: 0,
      totalItemsCount: 0,
      totalResultCount: 0,
      sources: [],
      timeline: []
    };
  }

  private trackEvent(eventName: string, payload: SeoUsageTrackingPayload): void {
    try {
      this.utils.logger.info('[SeoUsageTracking]', eventName, payload);
    } catch {
      // Tracking must never block user flow.
    }
  }
}
